import { FC, useMemo } from 'react'
import { Report } from 'common/Report/Report'
import { ChartData, ChartOptions } from 'chart.js'
import { Pie } from 'react-chartjs-2'
import { WithReportLoader } from 'common/WithLoader/WithLoader'
import { useTransitionId } from 'context/TransitionContext'
import {
  useGetTransitionItemsByPriorityReportQuery,
  useUpdateTransitionItemsByPriorityReportNotesMutation
} from 'services/api/transitionApi'
import { ChartReportItem } from 'types'
import { queryPriorityColors } from 'utils/queries'

const getConfig = (): ChartOptions => ({
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom'
    }
  }
})

const initialChartData = (backgroundColors: string[], categoryPercentage?: number): ChartData => ({
  labels: [],
  datasets: [
    {
      maxBarThickness: 100,
      categoryPercentage,
      data: [],
      backgroundColor: backgroundColors
    }
  ]
})

function GetPercentage (mainNumberOfItems: number, allItems: number) {
  if (allItems === 0) {
    return 0
  }

  return Math.round(mainNumberOfItems * 10000 / allItems) / 100
}

const chartData = (data: ChartReportItem[]): ChartData => {
  const dataLength = data.length
  const addCategoryThickness = dataLength < 10
  const sum = data.reduce((accumulator, currentValue) => accumulator + currentValue.value, 0)
  const backgroundColors: string[] = []

  return data.reduce<ChartData>((cd, { unit, value }) => {
    backgroundColors.push(queryPriorityColors.get(unit) ?? '')
    cd.labels?.push(`${unit} - ${GetPercentage(value, sum)}%`)
    cd.datasets[0].data.push(value)
    return cd
  }, initialChartData(backgroundColors, addCategoryThickness ? dataLength * 0.1 : 1))
}

export const ItemsByPriorityPieChartReport: FC = () => {
  const transitionId = useTransitionId()
  const { data: itemsByPriorityReport, isLoading } = useGetTransitionItemsByPriorityReportQuery(transitionId)
  const [updateNotes, { originalArgs }] = useUpdateTransitionItemsByPriorityReportNotesMutation()
  const notes = originalArgs?.notes ?? itemsByPriorityReport?.notes ?? ''
  const ChartElement = useMemo(() => {
    return <Pie type={'pie'} data={chartData(itemsByPriorityReport?.chartReportItems ?? [])} options={getConfig() } />
  }, [itemsByPriorityReport, getConfig()])

  return (
    <Report
      title={'Open items by Priority'}
      info={'Open items grouped by Priority.'}
      reportNote={{
        note: notes,
        saveNote: (notes) => updateNotes({ transitionId, notes })
      }}>
      <WithReportLoader
        noWrapper
        loading={isLoading}
        hasResults={(itemsByPriorityReport?.chartReportItems ?? []).length > 0}>
        {ChartElement}
      </WithReportLoader>
    </Report>
  )
}
